import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { HiMenu } from 'react-icons/hi';
import { ChevronDownIcon, ChatBubbleLeftIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useRoleTranslation } from '../../hooks/useRoleTranslation';
import { apiClient } from '../../services/apiClient';
import FeedbackModal from './FeedbackModal';
import TermsModal from './TermsModal';
import logo from '../../assets/logo.svg';

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [termsAcceptedAt, setTermsAcceptedAt] = useState(null);
  const [loadingTermsStatus, setLoadingTermsStatus] = useState(true);
  const { t } = useTranslation('common');
  const { getRoleLabel } = useRoleTranslation();

  useEffect(() => {
    if (!user) {
      setLoadingTermsStatus(false);
      return;
    }

    const fetchTermsStatus = async () => {
      try {
        const profile = await apiClient.get('/api/profile');
        setTermsAcceptedAt(profile.terms_accepted_at || null);
      } catch (error) {
        console.error('Error fetching terms status:', error);
      } finally {
        setLoadingTermsStatus(false);
      }
    };

    fetchTermsStatus();
  }, [user]);

  const handleLogout = () => {
    logout();
  };

  const getTranslatedRole = (role) => {
    if (!role) return t('header.noGroupAssigned');
    return getRoleLabel(`role-${role.toLowerCase()}`);
  };

  const showTermsButton = user && !loadingTermsStatus && !termsAcceptedAt;

  return (
    <header className="bg-white px-6 py-4">
      <div className="grid grid-cols-3 items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 -ml-2"
            aria-label="Toggle menu"
          >
            <HiMenu className="w-6 h-6" />
          </button>
          <div className="flex items-center space-x-2">
            <img src={logo} alt="QualiteeMed.ai" className="h-6" />
          </div>
        </div>

        <div className="flex justify-center px-2">
          {showTermsButton && (
            <button
              type="button"
              onClick={() => setIsTermsOpen(true)}
              className="group relative inline-flex items-center gap-2.5 rounded-full border-2 border-dashed border-amber-400 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 px-4 pt-3 pb-2 text-xs sm:text-sm font-bold tracking-tight text-amber-950 shadow-md shadow-amber-200/70 transition-all hover:scale-[1.02] hover:border-amber-500 hover:from-amber-100 hover:via-orange-100 hover:to-amber-200 hover:shadow-lg hover:shadow-amber-300/60 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
            >
              <span className="absolute -top-3.5 left-3 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm">
                {t('terms.promoBadge')}
              </span>
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
              </span>
              <span>{t('terms.button')}</span>
            </button>
          )}
        </div>
        
        <div className="flex items-center justify-end space-x-4">
          {user && (
            <Menu as="div" className="relative">
              {({ open }) => (
                <>
                  <Menu.Button className="flex items-center space-x-3 focus:outline-none">
                    <div className="hidden md:flex w-8 h-8 rounded-full items-center justify-center bg-[rgb(197,214,248)]">
                      <span className="text-sm font-medium">{user.email[0].toUpperCase()}</span>
                    </div>
                    <div className="text-sm text-left">
                      <p className="font-medium text-gray-700">
                        <span className="md:hidden">{user.email.split('@')[0]}</span>
                        <span className="hidden md:inline">{user.email}</span>
                      </p>
                      <p className="text-gray-500">{getTranslatedRole(user.group)}</p>
                    </div>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                        open ? 'transform rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </Menu.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none z-50">
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/settings"
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } flex items-center px-4 py-2 text-sm text-gray-700`}
                            >
                              <svg className="mr-3 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                              </svg>
                              {t('header.settings')}
                            </Link>
                          )}
                        </Menu.Item>
                      </div>
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setIsFeedbackOpen(true)}
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } flex w-full items-center px-4 py-2 text-sm text-gray-700 min-w-[200px] whitespace-nowrap`}
                            >
                              <ChatBubbleLeftIcon className="mr-3 h-5 w-5 text-gray-400" />
                              {t('header.sendFeedback')}
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                            >
                              <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" />
                              {t('header.logout')}
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </>
              )}
            </Menu>
          )}
          <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
          <TermsModal
            isOpen={isTermsOpen}
            onClose={() => setIsTermsOpen(false)}
            onAccepted={(acceptedAt) => setTermsAcceptedAt(acceptedAt)}
          />
        </div>
      </div>
    </header>
  );
};

export default Header; 